import {
    useMatchRoute,
    useRouterState,
    useSearch,
} from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Button, Icon } from '@atoms'
import { GetPayablesSummaryRequestParams } from '@/hooks'
import { Dropdown } from '@/components/common/Dropdown/Dropdown'
import { MenuItem } from '../../../../components/common/MenuItem/MenuItem'
import { useBillingApi } from '../../../../hooks/useApi/useBillingApi/useBillingApi'
import { payablesSummarySearchSchema } from '@/schemas'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useState } from 'react'

const PayablesSummaryExportDropdown: React.FC<{
    filters: GetPayablesSummaryRequestParams
}> = ({ filters }) => {
    const { t } = useTranslation('translations')

    const search = useSearch({ strict: false })
    const validatedSearch = payablesSummarySearchSchema.parse(search)
    const { sortBy, order, page, recordsPerPage } = validatedSearch

    const { usePayablesSummaryQuery } = useBillingApi()
    const params = {
        ...filters,
        sortBy,
        order,
        offset: (page - 1) * recordsPerPage,
        limit: recordsPerPage,
    }
    const { data, downloadXlsx } = usePayablesSummaryQuery(params)
    const addAlert = useToastStore((state) => state.addAlert)
    const [isOpen, setIsOpen] = useState(false)

    return (
        <Dropdown
            isOpen={isOpen}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setIsOpen(!isOpen)
            }}
            name="payables-summary-export-dropdown"
            buttonProps={{
                name: 'export-payables-summary-dropdown',
                children: (
                    <>
                        {t('common.export_report_dropdown.label')}
                        <Icon size="small" name="keyboard_arrow_down" />
                    </>
                ),
                variant: 'outlinePrimary',
                size: 'large',
                disabled: !data?.items.length,
            }}
            buttonComponent={Button}
            horizontalAlign="right"
            dropdownClassname="py-3 !min-w-[auto]"
        >
            <MenuItem
                path="/"
                variant="ghost"
                label={t('common.export_report_dropdown.options.xls')}
                render={({ renderContents }) => (
                    <button
                        className="group w-full outline-none"
                        onClick={() => {
                            addAlert({
                                variant: 'success',
                                title: t(
                                    'reporting.export_report.success_toast.title'
                                ),
                                description: t(
                                    'reporting.export_report.success_toast.description'
                                ),
                            })

                            setIsOpen(false)

                            downloadXlsx().catch(() => {
                                addAlert({
                                    variant: 'error',
                                    title: t(
                                        'reporting.export_report.error_toast.title'
                                    ),
                                    description: t(
                                        'reporting.export_report.error_toast.description'
                                    ),
                                })
                            })
                        }}
                        data-testid="export-payables-summary-excel-button"
                    >
                        {renderContents()}
                    </button>
                )}
            />
        </Dropdown>
    )
}

const PayablesSummaryExportDropdownGuard = ({
    filters,
}: {
    filters: GetPayablesSummaryRequestParams
}) => {
    const matchRoute = useMatchRoute()
    const { location } = useRouterState()
    const isActive =
        location.pathname === '/billing-and-payments/payables-summary'

    const isPending = !!matchRoute({
        to: '/billing-and-payments/payables-summary',
        pending: true,
    })

    if (isPending || isActive) {
        return <PayablesSummaryExportDropdown filters={filters} />
    }

    return null
}

export { PayablesSummaryExportDropdownGuard as PayablesSummaryExportDropdown }
